/**
 * Structured, machine-runnable scripts for the "Follow test" mode — the steps
 * the UI engine performs (and narrates) to drive a lab as a real user would.
 *
 * Each lab keeps these alongside its human-readable steps so labs remain the
 * single source of truth: same journey, one version a person reads, one a
 * robot runs.
 *
 * Setup (creating/clearing the sandbox, switching to it) is done
 * programmatically by prepareLabSandbox() — it's not the thing under test. The
 * watched steps below are the genuine user actions.
 */
import { dataService } from '../services/dataService';
import { SANDBOX_PREFIX } from '../services/testRunner';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Ensure a clean sandbox workspace and switch the app to it, so the lab can be
// deployed into an empty canvas without touching the user's real workspaces.
export async function prepareLabSandbox() {
  const list = await dataService.getWorkspaces();
  const existing = list.find((w) => (w.name || '').startsWith(SANDBOX_PREFIX));
  const id = existing
    ? existing.id
    : await dataService.createWorkspace(SANDBOX_PREFIX, 'Disposable workspace for the in-app Test App. Safe to delete.');

  // Clear any pipeline from a previous run so the lab Deploy guard (empty
  // canvas) passes.
  await dataService.savePipeline(id, [], [], false);

  // Switch the app's active workspace to the sandbox (AppLayout listens for
  // this and preserves the selection thereafter).
  localStorage.setItem('activeWorkspaceId', id);
  window.dispatchEvent(new CustomEvent('projectly_workspaces_changed'));
  await sleep(1200); // let the shell re-scope to the sandbox
  return id;
}

// ── Lab scripts ──────────────────────────────────────────────────────────────
// Step vocabulary (see uiTestEngine.runStep): navigate | click | type | select
// | waitFor | expect | sleep. `say` is the narration shown to the user.

export const LAB_SCRIPTS = {
  // Lab 1 — Hello Collection: add a contact, watch the Inbox notification land.
  1: {
    name: 'Lab 1 — Hello Collection',
    steps: [
      { do: 'navigate', to: 'labs', say: 'Open the Labs page' },
      { do: 'click', testid: 'lab-deploy', say: 'Click “Deploy Lab 1 to Canvas”' },
      { do: 'waitFor', testid: 'collection-node', text: 'Contacts', timeout: 20000, say: 'Wait for the pipeline to appear on the canvas' },
      { do: 'click', testid: 'collection-node', text: 'Contacts', say: 'Open the Contacts collection' },
      { do: 'click', testid: 'cm-tab-records', say: 'Switch to the Records tab' },
      { do: 'click', testid: 'cm-add-record', say: 'Click “+ Add record”' },
      { do: 'type', sel: '[data-testid="cm-draft-row"] input.cm-cell-input', value: 'Test User', say: 'Type the contact name “Test User”' },
      { do: 'click', testid: 'cm-save-record', say: 'Click the green ✓ to save' },
      { do: 'navigate', to: 'inbox', say: 'Open the Inbox' },
      { do: 'expect', sel: '.inbox-item', contains: 'Test User', timeout: 30000, say: 'Confirm the notification arrived in the Inbox' },
    ],
  },

  // Lab 2 — Auto-Status Update: add a ticket with no status, watch an action
  // auto-set it to "Open" and a confirmation land in the Inbox.
  2: {
    name: 'Lab 2 — Auto-Status Update',
    steps: [
      { do: 'navigate', to: 'labs', say: 'Open the Labs page' },
      { do: 'click', sel: '.labs-nav-item', text: 'Auto-Status Update', say: 'Select Lab 2 (Auto-Status Update)' },
      { do: 'click', testid: 'lab-deploy', say: 'Click “Deploy Lab 2 to Canvas”' },
      { do: 'waitFor', testid: 'collection-node', text: 'Tickets', timeout: 20000, say: 'Wait for the pipeline to appear on the canvas' },
      { do: 'click', testid: 'collection-node', text: 'Tickets', say: 'Open the Tickets collection' },
      { do: 'click', testid: 'cm-tab-records', say: 'Switch to the Records tab' },
      { do: 'click', testid: 'cm-add-record', say: 'Click “+ Add record”' },
      { do: 'type', sel: '[data-testid="cm-draft-row"] input.cm-cell-input', value: 'Fix login bug', say: 'Type the ticket title “Fix login bug” (leave Status blank)' },
      { do: 'click', testid: 'cm-save-record', say: 'Click the green ✓ to save' },
      { do: 'navigate', to: 'inbox', say: 'Open the Inbox' },
      { do: 'expect', sel: '.inbox-item', contains: 'Auto-Updated', timeout: 30000, say: 'Confirm the “Ticket Auto-Updated” notification arrived' },
    ],
  },
};
